import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { 
  ChevronRight, AlertCircle, CheckCircle, Mail, Camera, ShoppingCart,
  ThermometerSun, Zap, Volume2, TrendingDown, Sun, Thermometer, Shield, Leaf, FileDown,
  Package, Ruler, Palette, Wrench, Check, Truck, Star, FileText, Download
} from 'lucide-react';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { trackProductView, trackAddToCart, trackViewItem } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format description text with markdown-like sections
const formatDescription = (text) => {
  if (!text) return null;
  
  // Split by section headers (** ... **)
  const sections = text.split(/\*\*([A-ZÀÂÄÉÈÊËÏÎÔÙÛÜÇ\s']+)\*\*/g);
  
  if (sections.length <= 1) {
    // No sections found, return plain text with line breaks
    return text.split('\n').map((line, i) => (
      <span key={i}>
        {line.startsWith('•') ? (
          <span className="flex items-start gap-2 my-1">
            <span className="text-[#1E3A5F] mt-1">•</span>
            <span>{line.substring(1).trim()}</span>
          </span>
        ) : line}
        {i < text.split('\n').length - 1 && <br />}
      </span>
    ));
  }
  
  const result = [];
  for (let i = 0; i < sections.length; i++) {
    const part = sections[i].trim();
    if (!part) continue;
    
    // Check if this is a section title (all caps or title-like)
    if (/^[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜÇ\s']+$/.test(part) && part.length < 50) {
      result.push(
        <h3 key={`title-${i}`} className="font-semibold text-[#1E3A5F] mt-5 mb-2 first:mt-0">
          {part}
        </h3>
      );
    } else {
      // Content section - handle bullet points and line breaks
      const lines = part.split('\n').filter(l => l.trim());
      result.push(
        <div key={`content-${i}`} className="text-[#475569] leading-relaxed">
          {lines.map((line, j) => {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('•')) {
              return (
                <div key={j} className="flex items-start gap-2 my-1">
                  <span className="text-[#1E3A5F] mt-0.5">•</span>
                  <span>{trimmedLine.substring(1).trim()}</span>
                </div>
              );
            }
            return <p key={j} className="my-1">{trimmedLine}</p>;
          })}
        </div>
      );
    }
  }
  return result;
};

const SUBCATEGORY_TITLES = {
  'pompes-circulation': 'Pompes de circulation',
  'pompes-massage': 'Pompes de massage',
  'blower': 'Blower / Soufflerie',
  'filtres': 'Filtres et cartouches',
  'produits-traitement': 'Produits de traitement'
};

// Heat pump specific info by power
const HEAT_PUMP_INFO = {
  3: {
    climate: 'Climat chaud',
    climateIcon: Sun,
    usage: 'Climat chaud ou utilisation modérée, en complément du chauffage du spa'
  },
  5: {
    climate: 'Climat tempéré',
    climateIcon: Thermometer,
    usage: 'Solution polyvalente, majorité des situations'
  },
  7: {
    climate: 'Climat tempéré',
    climateIcon: Thermometer,
    usage: 'Grands volumes d\'eau et spas de nage'
  }
};

// Technical specs from Poolex PDF
const HEAT_PUMP_SPECS = {
  3: {
    puissance_calorifique: '3 kW',
    puissance_frigorifique: '2,11 kW',
    puissance_absorbee: '0,61 kW',
    cop: '5,35',
    alimentation: 'Mono 220-240V / 50Hz',
    debit_eau: '1,4 m³/h',
    raccords: '32/38 mm',
    plage_chauffage: '10°C à 40°C',
    plage_refroidissement: '2°C à 30°C',
    plage_fonctionnement: '-7°C à 43°C',
    fluide: 'R32 (0,24 kg)',
    dimensions: '400 x 440 x 390 mm',
    poids: '27 kg',
    niveau_sonore_1m: '< 48 dB(A)',
    niveau_sonore_10m: '< 30 dB(A)'
  },
  5: {
    puissance_calorifique: '5 kW',
    puissance_frigorifique: '3,25 kW',
    puissance_absorbee: '0,89 kW',
    cop: '5,70',
    alimentation: 'Mono 220-240V / 50Hz',
    debit_eau: '2,15 m³/h',
    raccords: '32/38 mm',
    plage_chauffage: '10°C à 40°C',
    plage_refroidissement: '2°C à 30°C',
    plage_fonctionnement: '-7°C à 43°C',
    fluide: 'R32 (0,40 kg)',
    dimensions: '440 x 590 x 485 mm',
    poids: '33 kg',
    niveau_sonore_1m: '< 48 dB(A)',
    niveau_sonore_10m: '< 30 dB(A)'
  },
  7: {
    puissance_calorifique: '7 kW',
    puissance_frigorifique: '4,75 kW',
    puissance_absorbee: '1,27 kW',
    cop: '5,60',
    alimentation: 'Mono 220-240V / 50Hz',
    debit_eau: '3 m³/h',
    raccords: '32/38 mm',
    plage_chauffage: '10°C à 40°C',
    plage_refroidissement: '2°C à 30°C',
    plage_fonctionnement: '-7°C à 43°C',
    fluide: 'R32 (0,45 kg)',
    dimensions: '490 x 590 x 510 mm',
    poids: '42 kg',
    niveau_sonore_1m: '< 50 dB(A)',
    niveau_sonore_10m: '< 35 dB(A)'
  }
};

// Structured data for accessories
const ACCESSOIRE_SPECS = {
  'leve-couverture': {
    intro: "Accessoire conçu pour faciliter la manipulation de votre couverture de spa au quotidien.",
    getSpecs: (part) => {
      const specs = [];
      if (part.name.includes('Under') || part.name.includes('under')) {
        specs.push({ label: 'Type', value: 'Lève-couverture avec fixation sous le spa' });
        specs.push({ label: 'Installation', value: 'Se glisse sous le spa (Understyle)' });
      } else {
        specs.push({ label: 'Type', value: 'Lève-couverture manuel' });
        specs.push({ label: 'Fixation', value: 'Sur structure du spa' });
      }
      if (part.name.includes('hydraulique') || part.long_description?.includes('hydraulique')) {
        specs.push({ label: 'Assistance', value: 'Vérin hydraulique' });
      }
      specs.push({ label: 'Compatibilité', value: 'Spas rigides standards' });
      specs.push({ label: 'Matériau', value: 'Acier / Aluminium' });
      specs.push({ label: 'Garantie', value: '2 ans' });
      return specs;
    },
    advantages: [
      'Manipulation sans effort de la couverture',
      'Protège la couverture (pas de frottement au sol)',
      'Prolonge la durée de vie de votre couverture',
      'Installation simple, sans outillage complexe'
    ]
  },
  'escalier': {
    intro: "Accessoire de sécurité et de confort pour faciliter l'accès à votre spa.",
    getSpecs: (part) => {
      const specs = [];
      const is3Marches = part.name.includes('3 marches');
      specs.push({ label: 'Nombre de marches', value: is3Marches ? '3' : '2' });
      specs.push({ label: 'Matériau', value: 'Résine haute qualité' });
      specs.push({ label: 'Antidérapant', value: 'Oui' });
      specs.push({ label: 'Résistance UV', value: 'Oui' });
      specs.push({ label: 'Coloris disponibles', value: 'Coffee, Dark Grey, Orange Foncé' });
      if (part.long_description?.includes('57cm')) {
        specs.push({ label: 'Dimensions', value: 'L 57cm x H 42cm x P 52cm' });
      }
      specs.push({ label: 'Garantie', value: '2 ans' });
      return specs;
    },
    advantages: [
      'Accès sécurisé au spa',
      'Surface antidérapante',
      'Résiste aux intempéries et UV',
      'Ne nécessite aucun entretien'
    ]
  },
  'testeurs-electroniques': {
    intro: "Appareil connecté pour surveiller et analyser la qualité de l'eau de votre spa.",
    getSpecs: (part) => {
      const specs = [];
      specs.push({ label: 'Type', value: 'Analyseur d\'eau connecté' });
      specs.push({ label: 'Connectivité', value: 'WiFi / Bluetooth' });
      specs.push({ label: 'Mesures', value: 'pH, Température, ORP' });
      specs.push({ label: 'Compatibilité', value: 'Chlore, Brome, Oxygène actif' });
      specs.push({ label: 'Application', value: 'Gratuite (iOS / Android)' });
      specs.push({ label: 'Garantie', value: '2 ans' });
      return specs;
    },
    advantages: [
      'Suivi en temps réel de la qualité de l\'eau',
      'Recommandations personnalisées via l\'app',
      'Accès à distance (WiFi)',
      'Discret et facile d\'utilisation'
    ]
  },
  'couverture': {
    intro: "Couverture thermique pour maintenir la température de votre spa et réduire la consommation d'énergie.",
    getSpecs: (part) => {
      const specs = [];
      specs.push({ label: 'Type', value: 'Couverture thermique isolante' });
      specs.push({ label: 'Matériau', value: 'Mousse haute densité / PVC' });
      specs.push({ label: 'Isolation', value: 'Excellente' });
      specs.push({ label: 'Résistance UV', value: 'Oui' });
      specs.push({ label: 'Sur mesure', value: 'Disponible sur demande' });
      return specs;
    },
    advantages: [
      'Maintient la température de l\'eau',
      'Réduit la consommation d\'énergie',
      'Protège des débris et saletés',
      'Sécurise le spa (modèles verrouillables)'
    ]
  }
};

// Default accessoire specs
const DEFAULT_ACCESSOIRE = {
  intro: "Accessoire conçu pour améliorer le confort, la sécurité ou l'utilisation quotidienne de votre spa.",
  getSpecs: (part) => {
    const specs = [];
    if (part.brand) specs.push({ label: 'Marque', value: part.brand });
    if (part.model) specs.push({ label: 'Modèle', value: part.model });
    specs.push({ label: 'Garantie', value: '2 ans' });
    return specs;
  },
  advantages: [
    'Améliore le confort d\'utilisation',
    'Facilite l\'accès ou la manipulation',
    'Installation simple',
    'Adapté à un usage extérieur'
  ]
};

// Accessoire Compact Section - Short intro for right column
const AccessoireCompactSection = ({ part, subCategory }) => {
  const config = ACCESSOIRE_SPECS[subCategory] || DEFAULT_ACCESSOIRE;
  return (
    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
      <p className="text-slate-700 text-sm leading-relaxed">
        {config.intro}
      </p>
    </div>
  );
};

// Accessoire Full Width Section - Detailed view below fold
const AccessoireFullWidthSection = ({ part, subCategory }) => {
  const config = ACCESSOIRE_SPECS[subCategory] || DEFAULT_ACCESSOIRE;
  const specs = config.getSpecs(part);
  const advantages = config.advantages;

  return (
    <div className="mt-10 space-y-8">
      {/* Section title */}
      <div className="border-b border-slate-200 pb-4">
        <h2 className="text-xl font-bold text-slate-900">Informations détaillées</h2>
      </div>

      {/* Caractéristiques principales - Format liste claire */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-5 flex items-center gap-2">
          <Ruler className="w-5 h-5 text-blue-600" />
          Caractéristiques principales
        </h3>
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-1">
          {specs.map((spec, index) => (
            <div key={index} className="flex justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">{spec.label}</span>
              <span className="font-semibold text-slate-900">{spec.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Avantages / Usages */}
      <div className="bg-gradient-to-br from-green-50 to-slate-50 rounded-xl border border-green-100 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-5 flex items-center gap-2">
          <Star className="w-5 h-5 text-green-600" />
          Avantages
        </h3>
        <ul className="grid sm:grid-cols-2 gap-3">
          {advantages.map((advantage, index) => (
            <li key={index} className="flex items-start gap-3 bg-white/70 rounded-lg p-3">
              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700">{advantage}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Livraison France */}
      <div className="bg-blue-50 rounded-xl border border-blue-100 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Truck className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">🚚 Livraison France – offerte dès 80 € TTC</h3>
            <p className="text-slate-600 text-sm mt-1">
              Expédition sous 48-72h. Emballage soigné pour garantir l'intégrité du produit.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// PDF URL for technical documentation
const POOLEX_PDF_URL = 'https://customer-assets.emergentagent.com/job_8af745cb-4bee-4b3b-a39b-9c9a639852df/artifacts/i6qfead7_Fiche_Poolex_Spawer_Ospa_FR.pdf';

// Heat Pump Product Section Component - Compact version for right column (above fold)
const HeatPumpCompactSection = ({ part }) => {
  return (
    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
      <p className="text-slate-700 text-sm leading-relaxed">
        Pompe à chaleur conçue pour <strong>maintenir la température du spa</strong> tout 
        en limitant la consommation d'énergie sur la durée.
      </p>
    </div>
  );
};

// Heat Pump Full Width Section Component - Details below fold
const HeatPumpFullWidthSection = ({ part }) => {
  const powerKw = part.power_kw || 5;
  const info = HEAT_PUMP_INFO[powerKw] || HEAT_PUMP_INFO[5];
  const specs = HEAT_PUMP_SPECS[powerKw] || HEAT_PUMP_SPECS[5];
  const ClimateIcon = info.climateIcon;

  return (
    <div className="mt-10 space-y-8">
      {/* Section title */}
      <div className="border-b border-slate-200 pb-4">
        <h2 className="text-xl font-bold text-slate-900">Informations détaillées</h2>
      </div>

      {/* PDF Download Button - Very prominent orange CTA */}
      <div className="bg-gradient-to-r from-[#EA580C] to-[#F97316] rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-lg border-2 border-orange-400">
        <div className="flex items-center gap-4 text-white">
          <div className="bg-white/20 p-3 rounded-full">
            <FileDown className="w-8 h-8" />
          </div>
          <div>
            <p className="font-bold text-xl">📄 Fiche technique officielle</p>
            <p className="text-white/90 text-sm">Documentation constructeur Poolex - Téléchargement gratuit</p>
          </div>
        </div>
        <a 
          href={POOLEX_PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 px-8 py-4 bg-white text-orange-600 rounded-xl hover:bg-orange-50 transition-all font-bold text-lg shadow-xl hover:scale-105"
          data-testid="download-pdf-btn"
        >
          <FileDown className="w-6 h-6" />
          Télécharger le PDF
        </a>
      </div>

      {/* Technical Specifications - Full data from PDF */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="font-bold text-slate-900 text-lg mb-5">Caractéristiques techniques</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Puissance calorifique</span>
            <span className="font-semibold text-slate-900">{specs.puissance_calorifique}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Puissance frigorifique</span>
            <span className="font-semibold text-slate-900">{specs.puissance_frigorifique}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Consommation</span>
            <span className="font-semibold text-slate-900">{specs.puissance_absorbee}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">COP</span>
            <span className="font-semibold text-emerald-600">{specs.cop}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Alimentation</span>
            <span className="font-semibold text-slate-900">{specs.alimentation}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Débit requis</span>
            <span className="font-semibold text-slate-900">{specs.debit_eau}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Raccords hydrauliques</span>
            <span className="font-semibold text-slate-900">{specs.raccords}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Plage chauffage</span>
            <span className="font-semibold text-slate-900">{specs.plage_chauffage}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Plage refroidissement</span>
            <span className="font-semibold text-slate-900">{specs.plage_refroidissement}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Plage fonctionnement</span>
            <span className="font-semibold text-slate-900">{specs.plage_fonctionnement}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Fluide frigorigène</span>
            <span className="font-semibold text-slate-900">{specs.fluide}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Dimensions (L×P×H)</span>
            <span className="font-semibold text-slate-900">{specs.dimensions}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Poids</span>
            <span className="font-semibold text-slate-900">{specs.poids}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Niveau sonore (1m)</span>
            <span className="font-semibold text-slate-900">{specs.niveau_sonore_1m}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-600">Niveau sonore (10m)</span>
            <span className="font-semibold text-slate-900">{specs.niveau_sonore_10m}</span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Avantages - arguments corrigés */}
        <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
          <h3 className="font-bold text-slate-900 text-lg mb-4 flex items-center gap-2">
            <Leaf className="w-5 h-5 text-emerald-600" />
            Les avantages
          </h3>
          <ul className="space-y-3 text-slate-700">
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 font-bold mt-0.5">✓</span>
              <span>Réduction de la consommation électrique sur le long terme</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 font-bold mt-0.5">✓</span>
              <span>Maintien de la température avec moins d'énergie</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 font-bold mt-0.5">✓</span>
              <span>Fonction chauffage et refroidissement</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 font-bold mt-0.5">✓</span>
              <span>Compatible avec la majorité des installations</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 font-bold mt-0.5">✓</span>
              <span>Pilotage Wi-Fi disponible</span>
            </li>
          </ul>
        </div>

        {/* Usage recommandé */}
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h3 className="font-bold text-slate-900 text-lg mb-4 flex items-center gap-2">
            <ClimateIcon className="w-5 h-5 text-blue-600" />
            Usage recommandé
          </h3>
          <div className="space-y-3 text-slate-700">
            <p className="flex items-start gap-2">
              <span className="text-blue-500 font-bold mt-0.5">•</span>
              <span><strong>{info.climate}</strong> : {info.usage}</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-blue-500 font-bold mt-0.5">•</span>
              <span>Spas utilisés régulièrement</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-blue-500 font-bold mt-0.5">•</span>
              <span>Installations extérieures ou intérieures</span>
            </p>
          </div>
        </div>
      </div>

      {/* Achat et accompagnement */}
      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-white rounded-lg shadow-sm flex-shrink-0">
            <ShoppingCart className="w-6 h-6 text-slate-600" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900 mb-1">Achat et accompagnement</h3>
            <p className="text-slate-600 text-sm">
              Disponible à l'achat direct. Une étude personnalisée peut être proposée pour 
              vérifier la compatibilité et vous accompagner dans l'installation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Import centralized price formatter
import { formatPrice } from '../utils/formatPrice';

// Format price TTC in French format with TTC suffix
const formatPriceTTC = (price) => {
  if (!price || price === 0) return null;
  return formatPrice(price, true);
};

export default function PartDetailPage() {
  const { category, subcategory, slug } = useParams();
  const location = useLocation();
  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    hasPhoto: false
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });

  // Check if this is a water treatment product (no compatibility check needed)
  const isWaterTreatment = category === 'entretien-spa' || part?.main_category === 'entretien-spa';
  
  // Check if this is a heat pump product
  const isHeatPump = part?.main_category === 'pompes-a-chaleur' || 
                     category === 'pompes-a-chaleur-spa' || 
                     location.pathname.includes('pompes-a-chaleur');
  
  // Check if this is an accessoire product
  const isAccessoire = part?.main_category === 'accessoires' || 
                       category === 'accessoires' ||
                       ['leve-couverture', 'escalier', 'testeurs-electroniques', 'couverture'].includes(part?.sub_category);
  
  // Check if product can be added to cart (pieces-detachees, entretien-spa, accessoires, and pompes-a-chaleur - not spas)
  const canAddToCart = part && (
    part.main_category === 'pieces-detachees' || 
    part.main_category === 'entretien-spa' ||
    part.main_category === 'accessoires' ||
    part.main_category === 'pompes-a-chaleur' ||
    part.main_category === 'filtres-spa' ||
    category === 'pieces-detachees' ||
    category === 'entretien-spa' ||
    category === 'pieces-accessoires' ||
    category === 'pompes-a-chaleur-spa' ||
    category === 'filtres-spa'
  );

  // Add to cart handler
  const handleAddToCart = () => {
    if (!part || !part.price_ttc) {
      toast.error('Prix non disponible pour ce produit');
      return;
    }
    
    setAddingToCart(true);
    
    // Get current cart from localStorage
    const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
    
    // Check if product already in cart
    const existingIndex = cart.findIndex(item => item.slug === part.slug);
    
    if (existingIndex >= 0) {
      cart[existingIndex].quantity += 1;
    } else {
      cart.push({
        slug: part.slug,
        name: part.name,
        price_ttc: part.price_ttc,
        image: part.images?.[0] || null,
        sku: part.sku,
        quantity: 1
      });
    }
    
    localStorage.setItem('spa_cart', JSON.stringify(cart));
    
    // Track GA4 add_to_cart
    trackAddToCart({
      id: part.id || part.slug,
      slug: part.slug,
      name: part.name,
      price: part.price_ttc,
      category: part.sub_category || subcategory,
      brand: part.brand
    }, 1);
    
    // Dispatch event for cart update
    window.dispatchEvent(new Event('cartUpdated'));
    
    setTimeout(() => {
      setAddingToCart(false);
      
    }, 300);
  };

  useEffect(() => {
    fetchPart();
  }, [slug]);

  // Track product view when part is loaded
  useEffect(() => {
    if (part) {
      trackProductView({
        product_slug: part.slug,
        category: part.sub_category || subcategory,
        product_name: part.name
      });
    }
  }, [part, subcategory]);

  const fetchPart = async () => {
    try {
      const res = await fetch(`${API_URL}/api/parts/${slug}`);
      if (!res.ok) throw new Error('Pièce non trouvée');
      const data = await res.json();
      setPart(data);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ type: 'loading', message: 'Envoi en cours...' });

    // Determine if this is a heat pump study request or compatibility check
    const isHeatPumpRequest = part?.main_category === 'pompes-a-chaleur' || 
                              category === 'pompes-a-chaleur-spa' || 
                              location.pathname.includes('pompes-a-chaleur');

    const messageContent = isHeatPumpRequest 
      ? `[Demande étude personnalisée - Pompe à chaleur]\n\nProduit: ${part?.name}\nRéférence: ${part?.sku || 'N/A'}\n\nMessage du client:\n${formData.message}`
      : `[Vérification compatibilité] ${part?.name}\n\nPhoto étiquette jointe: ${formData.hasPhoto ? 'Oui' : 'Non'}\n\n${formData.message}`;

    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: isHeatPumpRequest ? 'etude-pac' : 'compatibilite',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: messageContent,
          product_id: part?.slug,
          product_name: part?.name,
          location: 'France métropolitaine'
        })
      });

      if (res.ok) {
        // Track form submission
        trackCompatibilityFormSubmit({
          product_slug: part?.slug || '',
          category: part?.sub_category || subcategory || ''
        });
        const successMessage = isHeatPumpRequest 
          ? 'Merci, votre demande d\'étude a bien été envoyée.'
          : 'Demande envoyée ! Nous vous recontactons rapidement.';
        setFormStatus({ type: 'success', message: successMessage });
        setFormData({ name: '', email: '', phone: '', message: '', hasPhoto: false });
        setTimeout(() => setShowForm(false), 3000);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (err) {
      setFormStatus({ type: 'error', message: 'Erreur lors de l\'envoi. Veuillez réessayer.' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F]"></div>
      </div>
    );
  }

  if (!part) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-4">Pièce non trouvée</h1>
          <Link to="/pieces-detachees" className="text-[#1E3A5F] hover:underline font-medium">
            ← Retour aux pièces détachées
          </Link>
        </div>
      </div>
    );
  }

  const isPending = part.data_status?.includes('PENDING');
  const isOk = part.data_status === 'OK_FROM_BOOSPA';
  
  // Get price TTC
  const priceTTC = part.price_ttc || part.price;
  const displayPrice = formatPriceTTC(priceTTC);

  const breadcrumbs = [
    { name: 'Accueil', url: '/' },
    { name: category === 'pieces-detachees' ? 'Pièces détachées' : 'Accessoires', url: `/${category}` },
    { name: SUBCATEGORY_TITLES[subcategory] || subcategory, url: `/${category}/${subcategory}` },
    { name: part.name, url: `/${category}/${subcategory}/${slug}` }
  ];

  // Product schema for SEO - With real price TTC
  // Build SEO-optimized title and description
  const seoTitle = part.seo_title || `${part.name} | Pièce spa – Livraison France | Atlantique Spa Boutique`;
  const seoDescription = part.meta_description || 
    `${part.short_description || part.name}. ${part.brand ? `Marque ${part.brand}.` : ''} Livraison France entière. Pièces détachées et accessoires pour spa.`;

  // JSON-LD Product schema for Google
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": part.h1 || part.name,
    "description": part.long_description || part.short_description || seoDescription,
    "image": part.images?.length > 0 ? part.images[0] : undefined,
    "sku": part.sku,
    "brand": {
      "@type": "Brand",
      "name": part.brand || "Compatible Spa"
    },
    "offers": {
      "@type": "Offer",
      "url": `https://atlantiquespaboutique.com/${category}/${subcategory}/${part.slug}`,
      "priceCurrency": "EUR",
      "price": priceTTC ? priceTTC.toFixed(2) : undefined,
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "availability": part.availability === 'en_stock' 
        ? "https://schema.org/InStock" 
        : "https://schema.org/PreOrder",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Atlantique Spa Boutique",
        "url": "https://atlantiquespaboutique.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "FR"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 2,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          }
        }
      }
    },
    "category": subcategory === 'pompes-a-chaleur' ? 'Pompes à chaleur spa' :
                subcategory === 'filtres' ? 'Filtres spa' :
                subcategory === 'pompes-circulation' ? 'Pompes de circulation spa' :
                'Pièces détachées spa'
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={part.images?.length > 0 ? part.images[0] : undefined}
        canonical={`/${category}/${subcategory}/${part.slug}`}
        breadcrumbs={breadcrumbs}
        customSchema={productSchema}
      />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <nav className="flex items-center gap-2 text-sm text-[#64748B] overflow-x-auto">
            <Link to="/" className="hover:text-[#1E3A5F] whitespace-nowrap">Accueil</Link>
            <ChevronRight className="w-4 h-4 flex-shrink-0" />
            <Link to={`/${category}`} className="hover:text-[#1E3A5F] whitespace-nowrap">
              {category === 'pieces-detachees' ? 'Pièces détachées' : 'Accessoires'}
            </Link>
            <ChevronRight className="w-4 h-4 flex-shrink-0" />
            <Link to={`/${category}/${subcategory}`} className="hover:text-[#1E3A5F] whitespace-nowrap">
              {SUBCATEGORY_TITLES[subcategory] || subcategory}
            </Link>
            <ChevronRight className="w-4 h-4 flex-shrink-0" />
            <span className="text-[#0F172A] truncate font-medium">{part.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Left: Image */}
          <div>
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
              {/* Image - NO CROPPING */}
              <div className="aspect-square bg-white flex items-center justify-center">
                {part.images?.length > 0 ? (
                  <img src={part.images[0]} alt={part.name} className="h-full w-full object-contain p-6" />
                ) : (
                  <span className="text-9xl opacity-30">
                    {part.product_type === 'filtre' ? '🔍' : 
                     part.product_type === 'piece_detachee' ? '⚙️' : '🧪'}
                  </span>
                )}
              </div>
            </div>

            {/* Status badge */}
            {isPending && (
              <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="pending-badge">
                <div className="flex gap-4">
                  <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">Caractéristiques détaillées : en cours de validation</p>
                    <p className="text-base text-amber-700 mt-2 leading-relaxed">
                      Contactez-nous avec une photo de l'étiquette de votre pièce actuelle pour vérifier la compatibilité.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Info */}
          <div>
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-7">
              {/* Brand */}
              <p className="text-sm text-[#64748B] uppercase tracking-wide font-medium">{part.brand}</p>
              
              {/* Title */}
              <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] mt-2" data-testid="part-title" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {part.h1 || part.name}
              </h1>

              {/* SKU */}
              <p className="text-base text-[#64748B] mt-3">Réf: {part.sku}</p>

              {/* Heat Pump: Short intro only in right column */}
              {isHeatPump ? (
                <div className="mt-4">
                  <HeatPumpCompactSection part={part} />
                </div>
              ) : isAccessoire ? (
                <div className="mt-4">
                  <AccessoireCompactSection part={part} subCategory={part.sub_category || subcategory} />
                </div>
              ) : (
                <>
                  {/* Standard Description with formatted sections */}
                  {(part.long_description || part.short_description) && (
                    <div className="mt-5 text-base" data-testid="product-description">
                      {formatDescription(part.long_description || part.short_description)}
                    </div>
                  )}

                  {/* Specs - Only show if OK status AND no technical_specs (avoids duplication) */}
                  {isOk && !part.technical_specs && (
                    <div className="mt-7 space-y-4" data-testid="full-specs">
                      <h2 className="font-semibold text-[#0F172A] text-lg">Caractéristiques techniques</h2>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {part.power_hp && (
                          <div className="bg-[#F8FAFC] p-4 rounded-xl">
                            <span className="text-[#64748B]">Puissance</span>
                            <p className="font-semibold text-[#0F172A]">{part.power_hp} HP ({part.power_kw} kW)</p>
                          </div>
                        )}
                        {part.voltage_v && (
                          <div className="bg-[#F8FAFC] p-4 rounded-xl">
                            <span className="text-[#64748B]">Tension</span>
                            <p className="font-semibold text-[#0F172A]">{part.voltage_v}V / {part.frequency_hz}Hz</p>
                          </div>
                        )}
                        {part.flow_lpm && (
                          <div className="bg-[#F8FAFC] p-4 rounded-xl">
                            <span className="text-[#64748B]">Débit</span>
                            <p className="font-semibold text-[#0F172A]">{part.flow_lpm} L/min</p>
                          </div>
                        )}
                        {part.speed_type && (
                          <div className="bg-[#F8FAFC] p-4 rounded-xl">
                            <span className="text-[#64748B]">Type</span>
                            <p className="font-semibold text-[#0F172A]">{part.speed_type === 'bivitesse' ? 'Bi-vitesse' : 'Mono-vitesse'}</p>
                          </div>
                        )}
                        {part.union_inner_mm && (
                          <div className="bg-[#F8FAFC] p-4 rounded-xl">
                            <span className="text-[#64748B]">Raccords</span>
                            <p className="font-semibold text-[#0F172A]">{part.union_inch}" ({part.union_inner_mm} mm)</p>
                          </div>
                        )}
                        {part.includes_unions && (
                          <div className="bg-[#F8FAFC] p-4 rounded-xl">
                            <span className="text-[#64748B]">Raccords inclus</span>
                            <p className="font-semibold text-[#10B981]">✓ Oui</p>
                          </div>
                        )}
                        {part.diameter_mm && (
                          <div className="bg-[#F8FAFC] p-4 rounded-xl">
                            <span className="text-[#64748B]">Diamètre</span>
                            <p className="font-semibold text-[#0F172A]">{part.diameter_mm} mm</p>
                          </div>
                        )}
                        {part.length_mm && (
                          <div className="bg-[#F8FAFC] p-4 rounded-xl">
                            <span className="text-[#64748B]">Longueur</span>
                            <p className="font-semibold text-[#0F172A]">{part.length_mm} mm</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Notes */}
                      {part.plumbing_notes && (
                        <p className="text-base text-[#475569] bg-[#1E3A5F]/5 p-4 rounded-xl">
                          <strong className="text-[#1E3A5F]">Plomberie :</strong> {part.plumbing_notes}
                        </p>
                      )}
                      {part.electrical_notes && (
                        <p className="text-base text-[#475569] bg-[#1E3A5F]/5 p-4 rounded-xl">
                          <strong className="text-[#1E3A5F]">Électrique :</strong> {part.electrical_notes}
                        </p>
                      )}
                      {part.orientation_notes && (
                        <p className="text-base text-[#475569] bg-[#1E3A5F]/5 p-4 rounded-xl">
                          <strong className="text-[#1E3A5F]">Installation :</strong> {part.orientation_notes}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Compatibilité spa - Section SEO */}
                  {(part.main_category === 'pieces-detachees' || part.sub_category?.includes('pompe') || part.sub_category?.includes('rechauffeur') || part.sub_category?.includes('clavier') || part.sub_category?.includes('carte') || part.sub_category?.includes('blower')) && (
                    <div className="mt-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200 p-5" data-testid="compatibility-section">
                      <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <Check className="w-5 h-5 text-[#10B981]" />
                        Compatibilité spa
                      </h3>
                      <p className="text-sm text-[#64748B] mb-3">
                        Cette pièce est compatible avec les systèmes et marques suivantes :
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['Balboa', 'Gecko', 'Waterway', 'LX Whirlpool', 'Wellis', 'Canadian Spa', 'Hydropool'].map((brand) => (
                          <span key={brand} className="bg-white text-[#475569] text-xs px-3 py-1.5 rounded-full border border-[#E2E8F0] font-medium">
                            {brand}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-[#94A3B8] mt-3">
                        Vérifiez les dimensions et références avant commande. En cas de doute, contactez notre équipe technique.
                      </p>
                    </div>
                  )}

                  {/* Technical Specs - Filters and other products */}
                  {part.technical_specs && Object.keys(part.technical_specs).length > 0 && (
                    <div className="mt-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200 p-5" data-testid="technical-specs">
                      <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#1E3A5F]" />
                        Caractéristiques techniques
                      </h3>
                      
                      {/* Filter references section */}
                      {(part.technical_specs.ref_darlly || part.technical_specs.ref_unicel || part.technical_specs.ref_pleatco || part.technical_specs.ref_filbur) && (
                        <div className="mb-4 pb-4 border-b border-slate-200">
                          <p className="text-sm font-semibold text-slate-700 mb-2">Références compatibles</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(part.technical_specs)
                              .filter(([key]) => key.startsWith('ref_'))
                              .map(([key, value]) => {
                                const brandName = key.replace('ref_', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                return (
                                  <span key={key} className="bg-white text-slate-700 text-xs px-3 py-1.5 rounded-full border border-slate-200 font-medium">
                                    {brandName} : {value}
                                  </span>
                                );
                              })}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {Object.entries(part.technical_specs)
                          .filter(([key]) => !key.startsWith('ref_'))
                          .map(([key, value]) => {
                          const labels = {
                            puissance_restituee: 'Puissance restituée',
                            cop: 'COP (efficacité)',
                            plage_temperature_air: 'Plage température air',
                            alimentation: 'Alimentation',
                            puissance_consommee: 'Puissance consommée',
                            debit_eau: 'Débit d\'eau',
                            niveau_sonore: 'Niveau sonore',
                            dimensions: 'Dimensions (L×H×P)',
                            poids: 'Poids',
                            refrigerant: 'Réfrigérant',
                            echangeur: 'Échangeur',
                            raccords: 'Raccords',
                            fonction_chiller: 'Fonction Chiller',
                            controle_wifi: 'Contrôle WiFi',
                            diametre_exterieur: 'Diamètre extérieur',
                            longueur: 'Longueur',
                            diametre_interieur: 'Diamètre intérieur',
                            haut: 'Haut',
                            bas: 'Bas',
                            nombre_de_plis: 'Nombre de plis',
                            surface_filtrante: 'Surface filtrante',
                            compatibilite: 'Compatibilité'
                          };
                          return (
                            <div key={key} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                              <span className="text-sm text-slate-600">{labels[key] || key}</span>
                              <span className="text-sm font-semibold text-slate-900">{value}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Warranty Info for Heat Pumps */}
                  {part.warranty && Object.keys(part.warranty).length > 0 && (
                    <div className="mt-4 bg-green-50 rounded-xl border border-green-200 p-5" data-testid="warranty-info">
                      <h3 className="text-lg font-bold text-green-800 mb-3 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-green-600" />
                        Garanties constructeur
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {part.warranty.pompe_chaleur && (
                          <div className="bg-white rounded-lg p-3 text-center border border-green-100">
                            <p className="text-xs text-green-600 uppercase tracking-wider">Pompe à chaleur</p>
                            <p className="text-lg font-bold text-green-800">{part.warranty.pompe_chaleur}</p>
                          </div>
                        )}
                        {part.warranty.compresseur && (
                          <div className="bg-white rounded-lg p-3 text-center border border-green-100">
                            <p className="text-xs text-green-600 uppercase tracking-wider">Compresseur</p>
                            <p className="text-lg font-bold text-green-800">{part.warranty.compresseur}</p>
                          </div>
                        )}
                        {part.warranty.serpentin_titane && (
                          <div className="bg-white rounded-lg p-3 text-center border border-green-100">
                            <p className="text-xs text-green-600 uppercase tracking-wider">Serpentin titane</p>
                            <p className="text-lg font-bold text-green-800">{part.warranty.serpentin_titane}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Technical Doc Download Button */}
                  {part.technical_doc_url && (
                    <div className="mt-4">
                      <a
                        href={part.technical_doc_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-3 bg-[#1E3A5F] hover:bg-[#152C4A] text-white rounded-xl font-medium transition-colors shadow-sm"
                        data-testid="download-tech-doc"
                      >
                        <Download className="w-5 h-5" />
                        Télécharger la fiche technique (PDF)
                      </a>
                    </div>
                  )}
                </>
              )}

              {/* Price box - TTC Display */}
              <div className="mt-7 bg-[#1E3A5F]/5 border border-[#1E3A5F]/20 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#1E3A5F] font-medium">Prix TTC</p>
                    {displayPrice ? (
                      <p className="text-3xl font-bold text-[#0F172A]" data-testid="product-price-ttc">
                        {displayPrice}
                      </p>
                    ) : (
                      <p className="text-lg text-[#64748B] italic">Prix bientôt disponible</p>
                    )}
                  </div>
                  <span className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                    part.availability === 'en_stock' 
                      ? 'bg-[#10B981]/10 text-[#10B981]' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {part.availability === 'en_stock' ? 'En stock' : 'Sur commande'}
                  </span>
                </div>
                {displayPrice && (
                  <p className="text-xs text-[#64748B] mt-2">
                    🚚 Livraison France entière – Offerte dès 80 € TTC
                  </p>
                )}
              </div>

              {/* CTA Buttons - Clear hierarchy: Buy > Verify compatibility */}
              <div className="mt-7 space-y-5">
                {/* Primary: Add to Cart button for pieces and accessories - only if price > 0 */}
                {canAddToCart && part.price_ttc && part.price_ttc > 0 ? (
                  <button
                    onClick={handleAddToCart}
                    disabled={addingToCart}
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-3 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-70 text-lg"
                    data-testid="btn-add-to-cart"
                  >
                    <ShoppingCart className="w-6 h-6" />
                    {addingToCart ? 'Ajout en cours...' : 'Ajouter au panier'}
                  </button>
                ) : canAddToCart && (!part.price_ttc || part.price_ttc <= 0) ? (
                  <div className="space-y-2">
                    <button
                      disabled
                      className="w-full bg-slate-300 text-slate-500 font-semibold py-4 px-6 rounded-xl cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="btn-add-to-cart-disabled"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      Prix en cours de mise à jour
                    </button>
                    <p className="text-xs text-slate-500 text-center">
                      Utilisez "Vérifier la compatibilité" pour nous contacter
                    </p>
                  </div>
                ) : null}

                {/* Secondary: For Heat Pumps - "Demander une étude personnalisée" */}
                {isHeatPump ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => setShowForm(true)}
                      className="w-full bg-white border-2 border-slate-300 text-[#1E3A5F] hover:border-[#1E3A5F] hover:bg-[#1E3A5F]/5 font-medium py-3.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2"
                      data-testid="btn-request-study"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Demander une étude personnalisée
                    </button>
                    <p className="text-xs text-slate-500 text-center">
                      Pour valider le choix du modèle selon votre spa et votre installation
                    </p>
                  </div>
                ) : (
                  /* Secondary: Compatibility check button - only for pieces-detachees (not water treatment) */
                  !isWaterTreatment && (
                    <div className="space-y-2">
                      <button
                        onClick={() => setShowForm(true)}
                        className="w-full bg-white border-2 border-slate-300 text-slate-700 hover:border-[#1E3A5F] hover:text-[#1E3A5F] font-medium py-3.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2"
                        data-testid="btn-verify-compatibility"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Vérifier la compatibilité
                      </button>
                      <p className="text-xs text-slate-500 text-center">
                        Un doute sur la référence ? Nous vérifions pour vous avant expédition
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Expert installation block - Adapt for heat pumps */}
            <div className="mt-7 bg-gradient-to-r from-[#1E3A5F] to-[#2A4A70] rounded-xl p-7 text-white" data-testid="expert-block">
              <h3 className="font-semibold text-xl flex items-center gap-3">
                <span className="text-2xl">🔧</span>
                {isHeatPump ? 'Installation par un professionnel' : 'Installation / Remplacement par un expert'}
              </h3>
              <p className="text-white/80 mt-4 leading-relaxed text-base">
                Toutes nos <strong className="text-white">pièces détachées, accessoires et pompes à chaleur</strong> sont expédiés dans toute la <strong className="text-white">France métropolitaine</strong>. Besoin d'un conseil ? Notre expert vous accompagne par email.
              </p>
              <a
                href="mailto:contact@atlantiquespaboutique.com"
                className="inline-flex items-center gap-2 mt-5 text-[#F97316] hover:text-orange-300 font-semibold"
              >
                <Mail className="w-5 h-5" />
                contact@atlantiquespaboutique.com
              </a>
            </div>

            {/* Conseils liés - Maillage interne SEO */}
            <div className="mt-7 bg-slate-50 rounded-xl p-6 border border-slate-200" data-testid="related-articles">
              <h3 className="font-semibold text-slate-900 mb-4">Conseils utiles</h3>
              <div className="space-y-3">
                <Link 
                  to="/conseils/remplacer-piece-spa-ou-reparer" 
                  className="block text-sm text-slate-600 hover:text-blue-600 transition-colors"
                >
                  → Quand remplacer une pièce plutôt que réparer ?
                </Link>
                <Link 
                  to="/conseils/fonctionnement-spa-filtration-pompes-chauffage" 
                  className="block text-sm text-slate-600 hover:text-blue-600 transition-colors"
                >
                  → Comprendre le fonctionnement d'un spa
                </Link>
                <Link 
                  to="/conseils/produits-entretien-spa-indispensables" 
                  className="block text-sm text-slate-600 hover:text-blue-600 transition-colors"
                >
                  → Produits d'entretien indispensables
                </Link>
              </div>
              <Link to="/conseils" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium mt-4">
                Tous nos conseils <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Heat Pump Full Width Section - Below the fold */}
      {isHeatPump && part && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <HeatPumpFullWidthSection part={part} />
        </div>
      )}

      {/* Accessoire Full Width Section - Below the fold */}
      {isAccessoire && part && !isHeatPump && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AccessoireFullWidthSection part={part} subCategory={part.sub_category || subcategory} />
        </div>
      )}

      {/* Compatibility Form Modal / Study Request Modal for Heat Pumps */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-7">
              <div className="flex justify-between items-start mb-5">
                <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {isHeatPump ? 'Demander une étude personnalisée' : 'Vérifier la compatibilité'}
                </h2>
                <button 
                  onClick={() => setShowForm(false)}
                  className="text-[#94A3B8] hover:text-[#0F172A] text-xl"
                >
                  ✕
                </button>
              </div>

              <p className="text-[#475569] text-base mb-6">
                {isHeatPump ? (
                  <>Étude pour <strong className="text-[#0F172A]">{part.name}</strong></>
                ) : (
                  <>Pour <strong className="text-[#0F172A]">{part.name}</strong></>
                )}
              </p>

              {formStatus.type === 'success' ? (
                <div className="bg-[#10B981]/10 text-[#10B981] p-5 rounded-xl flex items-center gap-3">
                  <CheckCircle className="w-6 h-6" />
                  <span className="font-medium">
                    {isHeatPump ? 'Merci, votre demande d\'étude a bien été envoyée.' : formStatus.message}
                  </span>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#0F172A] mb-2">Nom *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-xl focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F] text-base"
                      data-testid="input-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0F172A] mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-xl focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F] text-base"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0F172A] mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-xl focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F] text-base"
                      data-testid="input-phone"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                      Informations complémentaires
                    </label>
                    <textarea
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder={isHeatPump 
                        ? "Marque et modèle de votre spa, usage prévu (intérieur/extérieur), questions particulières..."
                        : "Marque et modèle de votre spa, référence actuelle de la pièce..."
                      }
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-xl focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F] text-base"
                      data-testid="input-message"
                    />
                  </div>

                  {/* Photo checkbox - only for non-heat-pump products */}
                  {!isHeatPump && (
                    <label className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-xl cursor-pointer border border-[#E2E8F0]">
                      <input
                        type="checkbox"
                        checked={formData.hasPhoto}
                        onChange={(e) => setFormData({ ...formData, hasPhoto: e.target.checked })}
                        className="w-5 h-5 text-[#1E3A5F] rounded"
                      />
                      <div className="flex items-center gap-2">
                        <Camera className="w-5 h-5 text-[#64748B]" />
                        <span className="text-base text-[#475569]">J'ai une photo de l'étiquette à envoyer</span>
                      </div>
                    </label>
                  )}

                  {formStatus.type === 'error' && (
                    <p className="text-red-600 text-sm font-medium">{formStatus.message}</p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus.type === 'loading'}
                    className="w-full bg-[#1E3A5F] hover:bg-[#152C4A] disabled:bg-[#94A3B8] text-white font-semibold py-4 px-6 rounded-xl transition-colors text-base"
                    data-testid="btn-submit"
                  >
                    {formStatus.type === 'loading' ? 'Envoi...' : 'Envoyer ma demande'}
                  </button>
                </form>
              )}

              <div className="mt-7 pt-5 border-t border-[#E2E8F0] text-center text-base text-[#64748B]">
                Ou contactez-nous directement : 
                <a href="mailto:contact@atlantiquespaboutique.com" className="text-[#1E3A5F] font-semibold ml-1">contact@atlantiquespaboutique.com</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
